import { usePostHog } from '@posthog/react';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { PostHogEvent } from '../../../enums/postHogEvents';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { store } from '../../../store/store';

const TAB_SELECTOR = '.gc-dg-tab-link';
const ACTIVE_TAB_SELECTOR = '.gc-dg-tab-link.active';
const PAYMENT_ERROR_SELECTOR = '#gc-dg-paymentError';
const TAB_SELECTION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

const PAYMENT_METHODS: Record<string, CheckoutPaymentMethod> = { ach: 'bank', card: 'cc' };

export type CheckoutPaymentMethod = 'bank' | 'cc';

export type CheckoutFormError = {
  errorField: string;
  errorMessage: string;
  errorSource: 'submit' | 'required' | 'invalid';
};

const toPaymentMethod = (tab: Element | null | undefined) =>
  PAYMENT_METHODS[tab?.getAttribute('data-tab') ?? ''];

const isShown = (element: Element | null) =>
  !!element && !element.classList.contains('gc-dg-hidden');

/**
 * @description PostHog events of the checkout test variant: the payment method picked, FAQ clicks
 * and form errors. The CTA event is captured on the SDK response by CheckoutPage, as in control.
 * @returns callbacks for the FAQ accordions and the field errors found by useFieldValidationState
 */
export const useCheckoutTracking = (panelRef: RefObject<HTMLElement | null>) => {
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;

  const capture = useCallback((eventName: PostHogEvent, properties: Record<string, unknown>) => {
    capturePostHogEvent(posthogRef.current, eventName, store.get(), properties);
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    let selectedMethod: CheckoutPaymentMethod | undefined;
    let isPaymentErrorShown = false;

    const selectMethod = (method: CheckoutPaymentMethod | undefined) => {
      if (!method || method === selectedMethod) return;
      selectedMethod = method;
      capture(PostHogEvent.CHECKOUT_PAYMENT_METHOD_SELECTED, { paymentMethod: method });
    };

    const handleClick = (event: MouseEvent) => {
      const tab = (event.target as Element).closest(TAB_SELECTOR);
      if (tab) selectMethod(toPaymentMethod(tab));
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Element;
      if (!target.closest(TAB_SELECTOR) || !TAB_SELECTION_KEYS.includes(event.key)) return;
      queueMicrotask(() => selectMethod(toPaymentMethod(panel.querySelector(ACTIVE_TAB_SELECTOR))));
    };

    const trackPaymentError = () => {
      const paymentError = panel.querySelector(PAYMENT_ERROR_SELECTOR);
      const isShownNow = isShown(paymentError);
      if (isShownNow && !isPaymentErrorShown) {
        capture(PostHogEvent.CHECKOUT_FORM_ERROR, {
          errorField: 'payment',
          errorMessage: paymentError?.textContent?.trim(),
          errorSource: 'submit',
        });
      }
      isPaymentErrorShown = isShownNow;
    };

    const observer = new MutationObserver(trackPaymentError);
    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    panel.addEventListener('click', handleClick, true);
    panel.addEventListener('keydown', handleKeyDown, true);

    return () => {
      observer.disconnect();
      panel.removeEventListener('click', handleClick, true);
      panel.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [panelRef, capture]);

  const trackFaqToggle = useCallback(
    (faqQuestion: string, faqExpanded: boolean) =>
      capture(PostHogEvent.CHECKOUT_FAQ_CLICKED, { faqQuestion, faqExpanded }),
    [capture],
  );

  const trackFormError = useCallback(
    (error: CheckoutFormError) => capture(PostHogEvent.CHECKOUT_FORM_ERROR, error),
    [capture],
  );

  return { trackFaqToggle, trackFormError };
};
